// ============================================================
// View: Muster & Serien — Wochentage, Tageszeit, Monate, Highlights
// ============================================================

import { useMemo } from 'react';
import { Card, CardTitle, NetCell, EurCell } from '../components/ui';
import { Icon } from '../icons';
import { CO } from '../theme';
import { useApp } from '../store';
import {
  computeSeries,
  inRange,
  kpi,
  monthOfYearBuckets,
  period,
  timeBuckets,
  weekdayBuckets,
} from '../aggregator';
import { dstr, eur, pct, sEur } from '../format';
import type { Kpi } from '../types';

export function Muster() {
  const { history, periodMode, customFrom, customTo, setDetailId } = useApp();
  const work = history?.eintraege ?? [];
  const p = useMemo(() => period(periodMode, work, customFrom, customTo), [periodMode, work, customFrom, customTo]);
  const fe = useMemo(() => (p.full ? work.slice() : work.filter((e) => inRange(e, p))), [work, p]);

  if (!fe.length) return null;

  const small = fe.length < 20;
  const wd = useMemo(() => weekdayBuckets(fe).map((x) => ({ label: x.label, ...kpi(x.entries) })), [fe]);
  const tb = useMemo(() => timeBuckets(fe).map((x) => ({ label: x.label, ...kpi(x.entries) })), [fe]);
  const moy = useMemo(
    () => monthOfYearBuckets(fe).map((x) => ({ label: x.label, ...kpi(x.entries) })).filter((r) => r.n > 0),
    [fe],
  );

  const chron = useMemo(() => fe.slice().sort((a, b) => (a.iso < b.iso ? -1 : 1)), [fe]);
  const series = useMemo(() => computeSeries(chron), [chron]);

  const byG = useMemo(() => chron.slice().sort((a, b) => b.gewinn - a.gewinn), [chron]);
  const byN = useMemo(
    () => chron.slice().sort((a, b) => b.gewinn - b.einsatz - (a.gewinn - a.einsatz)),
    [chron],
  );
  const byE = useMemo(() => chron.slice().sort((a, b) => b.einsatz - a.einsatz), [chron]);
  const worst = useMemo(
    () => chron.slice().sort((a, b) => a.gewinn - a.einsatz - (b.gewinn - b.einsatz)),
    [chron],
  );

  return (
    <div>
      <Note />
      {small ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            background: '#fdf6ec',
            border: '1px solid #eddcc0',
            borderRadius: 10,
            padding: '11px 16px',
            marginBottom: 18,
            fontSize: 12.5,
            color: '#8a5e12',
          }}
        >
          <Icon name="warn" color="#b8801f" size={16} /> Geringe Stichprobe: nur {fe.length} Einträge im gewählten
          Zeitraum. Einzelne Muster sind daher wenig aussagekräftig.
        </div>
      ) : null}

      <Card>
        <CardTitle title="Wochentage" sub={`${p.label} · ${fe.length} Einträge insgesamt`} />
        <PatternTable rows={wd} />
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
        <Card>
          <CardTitle title="Tageszeit" sub={`${p.label} · Verteilung nach Abgabezeitpunkt`} />
          <PatternTable rows={tb} />
        </Card>
        <Card>
          <CardTitle title="Kalendermonate" sub={p.label} />
          <PatternTable rows={moy} />
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
        <Card>
          <CardTitle title="Serien" sub={`${p.label} · chronologisch über ${chron.length} Einträge`} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <SerieRow
              label="Längste Folge ohne Auszahlung"
              value={`${series.noPay} Einträge`}
              def="Aufeinanderfolgende Einträge mit Auszahlung = 0 €."
            />
            <SerieRow
              label="Längste Folge ohne positives Nettoergebnis"
              value={`${series.noNet} Einträge`}
              def="Aufeinanderfolgende Einträge mit Auszahlung ≤ Einsatz."
            />
            <SerieRow
              label="Längste Folge mit mindestens einer Auszahlung"
              value={`${series.withPay} Einträge`}
              def="Aufeinanderfolgende Einträge mit Auszahlung > 0 €."
            />
            <SerieRow
              label="Durchschnittlicher Abstand zwischen Auszahlungen"
              value={
                series.avgGap == null
                  ? '–'
                  : `${series.avgGap.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Tage`
              }
              def="Mittlerer zeitlicher Abstand zwischen Einträgen mit Auszahlung > 0 €."
            />
            <SerieRow
              label="Einträge mit positivem Nettoergebnis"
              value={`${series.posCount} von ${chron.length}`}
              def="Auszahlung > Einsatz."
              last
            />
          </div>
        </Card>

        <Card>
          <CardTitle title="Einzelne historische Auffälligkeiten" sub={`${p.label} · jeweils genau 1 Eintrag`} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <HighlightRow
              label="Größte Auszahlung"
              e={byG[0]}
              value={eur(byG[0].gewinn)}
              color={CO.auszahlung}
              onClick={() => setDetailId(byG[0].id)}
            />
            <HighlightRow
              label="Größtes positives Nettoergebnis"
              e={byN[0]}
              value={sEur(byN[0].gewinn - byN[0].einsatz)}
              color={byN[0].gewinn - byN[0].einsatz >= 0 ? CO.pos : CO.neg}
              onClick={() => setDetailId(byN[0].id)}
            />
            <HighlightRow
              label="Höchster Einsatz"
              e={byE[0]}
              value={eur(byE[0].einsatz)}
              color={CO.einsatz}
              onClick={() => setDetailId(byE[0].id)}
            />
            <HighlightRow
              label="Stärkster Nettoverlust eines Eintrags"
              e={worst[0]}
              value={sEur(worst[0].gewinn - worst[0].einsatz)}
              color={CO.neg}
              onClick={() => setDetailId(worst[0].id)}
              last
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Note() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        background: '#eef2f5',
        border: '1px solid #d7e0e6',
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: 20,
      }}
    >
      <Icon name="warn" color="#4a6b7d" size={16} />
      <div style={{ fontSize: 12.5, color: '#3f5763', lineHeight: 1.45 }}>
        Diese Auswertungen beschreiben ausschließlich die importierte Vergangenheit. Sie erlauben keine Aussage über
        zukünftige Gewinnchancen.
      </div>
    </div>
  );
}

function PatternTable({ rows }: { rows: (Kpi & { label: string })[] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead>
        <tr style={{ color: CO.faint }}>
          {['', 'Einträge', 'Einsatz', 'Auszahlung', 'Nettoergebnis', 'Auszahlungsquote', 'Nettogewinnquote'].map(
            (c, i) => (
              <th
                key={i}
                style={{
                  textAlign: i ? 'right' : 'left',
                  fontWeight: 500,
                  padding: '0 8px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {c}
              </th>
            ),
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={i}
            style={{
              borderTop: `1px solid ${CO.border2}`,
              background: r.n === 0 ? '#fafbf9' : 'transparent',
            }}
          >
            <td style={{ padding: '8px', color: CO.text }}>{r.label}</td>
            <td className="num" style={{ textAlign: 'right', padding: '8px', color: CO.muted }}>
              {r.n}
            </td>
            <td style={{ textAlign: 'right', padding: '8px' }}>
              <EurCell value={r.einsatz} />
            </td>
            <td style={{ textAlign: 'right', padding: '8px' }}>
              <EurCell value={r.gewinn} tone="auszahlung" />
            </td>
            <td style={{ textAlign: 'right', padding: '8px' }}>
              <NetCell n={r.netto} />
            </td>
            <td className="num" style={{ textAlign: 'right', padding: '8px', color: CO.muted }}>
              {r.n ? pct(r.auszahlungsquote) : '–'}
            </td>
            <td className="num" style={{ textAlign: 'right', padding: '8px', color: CO.muted }}>
              {r.n ? pct(r.nettogewinnquote) : '–'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SerieRow({ label, value, def, last }: { label: string; value: string; def: string; last?: boolean }) {
  return (
    <div
      style={{
        padding: '11px 0',
        borderTop: last ? 'none' : `1px solid ${CO.border2}`,
        borderBottom: last ? `1px solid ${CO.border2}` : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontSize: 13, color: CO.text, fontWeight: 500 }}>{label}</span>
        <span className="num" style={{ fontSize: 14, fontWeight: 700, color: CO.text, whiteSpace: 'nowrap' }}>
          {value}
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: CO.faint, marginTop: 2 }}>Definition: {def}</div>
    </div>
  );
}

function HighlightRow({
  label,
  e,
  value,
  color,
  onClick,
  last,
}: {
  label: string;
  e: { id: string; spielart: string; iso: string };
  value: string;
  color: string;
  onClick: () => void;
  last?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '11px 0',
        borderTop: last ? 'none' : `1px solid ${CO.border2}`,
        borderBottom: last ? `1px solid ${CO.border2}` : 'none',
        border: 'none',
        background: 'transparent',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <div>
        <div style={{ fontSize: 13, color: CO.text, fontWeight: 500 }}>{label}</div>
        <div className="num" style={{ fontSize: 11.5, color: CO.faint, marginTop: 2 }}>
          {e.spielart} · {dstr(e.iso)}
        </div>
      </div>
      <span className="num" style={{ fontSize: 15, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
        {value}
      </span>
    </button>
  );
}
